import { t, type StaffKey, type StaffLanguage } from '../lib/staffText'
import { onCall, type ElementKind, type Fault, type LayoutDocument, type LayoutElement } from './types'

/**
 * A main display's Called screen (internal/layout/called.go).
 *
 * On the wire its elements are document.called.elements, so an older display build
 * ignores them. In the browser they sit in document.elements tagged screens ['call'],
 * so the editor's drawing, layers, undo and faults work on them as on any screen.
 * lib/api.ts unpacks every document it receives and packs every one it sends.
 */

export const CALLED_DEFAULT_SECONDS = 5
export const CALLED_MIN_SECONDS = 3
export const CALLED_MAX_SECONDS = 30

/** What the Called screen may hold (layout.CalledElements). */
export const CALLED_KINDS: readonly ElementKind[] = [
  'ticketNumber', 'serviceName', 'deskName', 'deskNumber', 'branchName', 'clock', 'dateTime', 'text', 'logo', 'image', 'rule', 'panel',
]

/** Kinds only a call fills: the Called screen's, never the board's. */
export const CALL_ONLY: readonly ElementKind[] = ['ticketNumber', 'serviceName', 'deskName', 'deskNumber']

export function hasCalled(doc: Pick<LayoutDocument, 'kind' | 'called'>): boolean {
  return doc.kind === 'display' && !!doc.called
}

/** Seconds a call shows: the design's, else 5; anything outside 3–30 is the default. */
export function calledSeconds(doc: Pick<LayoutDocument, 'called'>): number {
  const n = doc.called?.showSec ?? 0
  return Number.isInteger(n) && n >= CALLED_MIN_SECONDS && n <= CALLED_MAX_SECONDS ? n : CALLED_DEFAULT_SECONDS
}

/** The wire's document.called.elements moved into elements, tagged for the Called screen. */
export function unpackCalled<D extends LayoutDocument | undefined>(doc: D): D {
  if (!doc || !doc.called?.elements) return doc
  const { elements: called, ...rest } = doc.called
  return {
    ...doc,
    elements: [...(doc.elements ?? []), ...called.map((el) => ({ ...el, screens: ['call' as const] }))],
    called: rest,
  }
}

/** The browser's document as the server stores it: Called elements back under called. */
export function packCalled(doc: LayoutDocument): LayoutDocument {
  const mine = doc.elements.filter(onCall)
  if (!doc.called) return mine.length === 0 ? doc : { ...doc, elements: doc.elements.filter((el) => !onCall(el)) }
  const strip = ({ screens: _screens, ...el }: LayoutElement): LayoutElement => el
  return {
    ...doc,
    elements: doc.elements.filter((el) => !onCall(el)),
    called: { ...doc.called, elements: mine.length > 0 ? mine.map(strip) : (doc.called.elements ?? []) },
  }
}

/** The built-in Called screen on a 1920×1080 board, as the templates carry it
 *  (layout.calledScreen; called_test.go holds the two together). */
export const CALLED_STARTER: readonly LayoutElement[] = [
  { id: 'calledBranch', kind: 'branchName', x: 80, y: 56, w: 900, h: 68, z: 10, style: { align: 'left', weight: 700, colour: '--ink-strong', scale: 0.9 } },
  { id: 'calledClock', kind: 'clock', x: 1380, y: 56, w: 460, h: 68, z: 10, style: { align: 'right', colour: '--ink-muted', scale: 0.8 }, options: { format: 'HH:mm' } },
  { id: 'calledService', kind: 'serviceName', x: 160, y: 196, w: 1600, h: 96, z: 20, style: { align: 'center', weight: 600, colour: '--ink-muted', uppercase: true } },
  { id: 'calledNumber', kind: 'ticketNumber', x: 160, y: 300, w: 1600, h: 440, z: 20, style: { align: 'center', weight: 700, colour: '--accent-solid' } },
  { id: 'calledLine', kind: 'rule', x: 810, y: 772, w: 300, h: 8, z: 20, style: { gradient: true }, options: { thickness: 6 } },
  { id: 'calledDesk', kind: 'deskName', x: 160, y: 812, w: 1600, h: 170, z: 20, style: { align: 'center', weight: 700, colour: '--ink-strong' } },
]

/** The server's smallest side for an element (layout.MinSide). */
const MIN_SIDE = 8

/** A Called screen added: the templates' own, scaled to the canvas, in place of any stray Called element. */
export function addCalled(doc: LayoutDocument): LayoutDocument {
  if (doc.kind !== 'display' || doc.called) return doc
  const sx = doc.canvas.width / 1920
  const sy = doc.canvas.height / 1080
  const tail = Math.random().toString(36).slice(2, 8)
  const made = CALLED_STARTER.map((el): LayoutElement => ({
    ...el,
    id: `${el.id}-${tail}`,
    x: Math.round(el.x * sx),
    y: Math.round(el.y * sy),
    w: Math.max(MIN_SIDE, Math.round(el.w * sx)),
    h: Math.max(MIN_SIDE, Math.round(el.h * sy)),
    screens: ['call'],
  }))
  return { ...doc, called: { showSec: CALLED_DEFAULT_SECONDS }, elements: [...doc.elements.filter((el) => !onCall(el)), ...made] }
}

/** The Called screen and everything on it removed. */
export function removeCalled(doc: LayoutDocument): LayoutDocument {
  if (!doc.called && !doc.elements.some(onCall)) return doc
  const { called: _called, ...rest } = doc
  return { ...rest, elements: doc.elements.filter((el) => !onCall(el)) }
}

/** The Called screen's time set, kept within 3–30. */
export function withShowSec(doc: LayoutDocument, seconds: number): LayoutDocument {
  if (!doc.called) return doc
  const n = Math.round(Math.min(CALLED_MAX_SECONDS, Math.max(CALLED_MIN_SECONDS, seconds)))
  return { ...doc, called: { ...doc.called, showSec: n } }
}

/** What the server refuses about a display's two screens, in the editor's words. */
export function calledFaults(doc: LayoutDocument, lang: StaffLanguage = 'en'): Fault[] {
  const found: Fault[] = []
  const label = (el: LayoutElement) => t(`designs.el.${el.kind}` as StaffKey, lang)
  for (const el of doc.elements) {
    // Never silent: packCalled drops such an element on save.
    if (onCall(el) && !hasCalled(doc)) {
      found.push({ text: t('designs.fault.otherScreen', lang, { label: label(el) }), ids: [el.id] })
      continue
    }
    if (doc.kind !== 'display') continue
    if (onCall(el) && !CALLED_KINDS.includes(el.kind)) found.push({ text: t('designs.fault.notOnCalled', lang, { label: label(el) }), ids: [el.id] })
    if (!onCall(el) && CALL_ONLY.includes(el.kind)) found.push({ text: t('designs.fault.calledOnly', lang, { label: label(el) }), ids: [el.id] })
  }
  const s = doc.called?.showSec
  if (s !== undefined && s !== 0 && !(Number.isInteger(s) && s >= CALLED_MIN_SECONDS && s <= CALLED_MAX_SECONDS)) {
    found.push({ text: t('designs.fault.showFor', lang, { min: CALLED_MIN_SECONDS, max: CALLED_MAX_SECONDS }), ids: [] })
  }
  return found
}

/** What is worth saying without refusing: a Called screen with no number on it. */
export function calledWarnings(doc: LayoutDocument, lang: StaffLanguage = 'en'): Fault[] {
  if (!hasCalled(doc)) return []
  if (doc.elements.some((el) => onCall(el) && el.kind === 'ticketNumber' && !el.hidden)) return []
  return [{ text: t('designs.warn.calledNoNumber', lang), ids: [] }]
}
